import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { submitTestimonial } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useFormTracking } from "@/hooks/useFormTracking";

export default function TestimonialModal({ trigger }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    location: "",
    quote: "",
  });
  const formTrack = useFormTracking("testimonial");

  const change = (k) => (e) => {
    const v = e.target.value;
    setForm({ ...form, [k]: v });
    formTrack.onChange(k, v);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.quote.trim()) {
      toast.error("Please add your name and a short message.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        quote: form.quote.trim(),
      };
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.location.trim()) payload.location = form.location.trim();
      await submitTestimonial(payload);
      track("testimonial_submit", { has_email: Boolean(payload.email), has_location: Boolean(payload.location) });
      formTrack.markSubmitted();
      toast.success("Thank you. Your experience has been received.");
      setForm({ name: "", email: "", location: "", quote: "" });
      setOpen(false);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) track("share_experience_open");
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        data-testid="testimonial-modal"
        className="sm:max-w-xl bg-white border border-[#e5e7eb] rounded-none p-0"
      >
        <div className="p-8 md:p-10">
          <DialogHeader className="text-left mb-6">
            <span className="ash-eyebrow">Share Your Experience</span>
            <DialogTitle className="mt-3 text-3xl font-bold tracking-tight text-[#080808]">
              Tell us how we did.
            </DialogTitle>
            <DialogDescription className="text-[#3C4048] mt-3">
              Submissions are reviewed by our team before being published.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={onSubmit} className="grid gap-6">
            <div>
              <label className="ash-label" htmlFor="testimonial-name">
                Your Name
              </label>
              <input
                id="testimonial-name"
                data-testid="testimonial-name-input"
                className="ash-input"
                placeholder="Margaret H."
                value={form.name}
                onChange={change("name")}
                onFocus={() => formTrack.onFocus("name")}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="ash-label" htmlFor="testimonial-email">
                  Email (optional)
                </label>
                <input
                  id="testimonial-email"
                  data-testid="testimonial-email-input"
                  type="email"
                  className="ash-input"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={change("email")}
                  onFocus={() => formTrack.onFocus("email")}
                />
              </div>
              <div>
                <label className="ash-label" htmlFor="testimonial-location">
                  Location (optional)
                </label>
                <input
                  id="testimonial-location"
                  data-testid="testimonial-location-input"
                  className="ash-input"
                  placeholder="Hamilton"
                  value={form.location}
                  onChange={change("location")}
                  onFocus={() => formTrack.onFocus("location")}
                />
              </div>
            </div>

            <div>
              <label className="ash-label" htmlFor="testimonial-quote">
                Your Experience
              </label>
              <textarea
                id="testimonial-quote"
                data-testid="testimonial-quote-input"
                className="ash-input ash-textarea"
                placeholder="Share a few sentences about your treatment outcome…"
                value={form.quote}
                onChange={change("quote")}
                onFocus={() => formTrack.onFocus("quote")}
              />
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="ash-link-underline"
                data-testid="testimonial-cancel-button"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="ash-btn-primary disabled:opacity-60"
                data-testid="testimonial-submit-button"
              >
                {loading ? "Submitting…" : "Submit"}
              </button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
